import { useContext } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { UserContext } from "../UserContext.js";

/**
 * Gate for every screen that needs a signed-in user.
 *
 * Before this existed there was no route guard in the app at all. The two
 * <Routes> blocks in App.js are split by `showNav`, which is a chrome test
 * (is this a login or a charcode page), not an auth test, so navigating
 * straight to /zones or /admin/farmers rendered the screen for anyone. The
 * only redirect that had ever been written sat inside the initUser effect and
 * was dead code: its condition re-tested /forgot-password, which the same
 * chain had already returned from, so it could never fire.
 *
 * Nothing leaked, because bbb-server applies jwtParser globally and the data
 * calls came back 401, but the app rendered its empty state to a stranger.
 *
 * Deliberately a wrapper rather than a navigate() in an effect: the effect
 * approach paints the screen and then redirects, so a farmer sees a flash of
 * a screen they cannot use.
 */

/**
 * Only an internal path is safe to send someone back to after login. A value
 * starting `//` or carrying a scheme is an open redirect, so it is discarded
 * rather than sanitised.
 *
 * @param {unknown} path
 * @returns {boolean}
 */
export function isSafeReturnPath(path) {
  return (
    typeof path === "string" &&
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.includes("\\")
  );
}

const RequireAuth = ({ children }) => {
  const context = useContext(UserContext);
  const location = useLocation();

  const user = context?.user;

  // initUser resolves the stored token against /refresh-session
  // asynchronously and leaves `authed` undefined until it has answered.
  // Treating that window as "not authenticated" would sign out every user who
  // has a valid token, so it is a third state and not a redirect.
  if (user?.authed === undefined) return null;

  if (user.authed) return children ?? <Outlet />;

  const from = location.pathname + location.search;

  return (
    <Navigate
      to="/login"
      replace
      state={isSafeReturnPath(from) ? { from } : undefined}
    />
  );
};

export default RequireAuth;
